import { useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';

interface CurrentUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: 'user' | 'admin' | 'moderator';
  isAuthenticated: boolean;
  loginTime: string;
}

const STORAGE_KEY = 'currentUser';

// Module-level store so every component using useAuth sees the same user.
// Previously each component kept its own copy read once on mount, so the
// navigation (mounted while signed out) never noticed a sign-in.
const readStoredUser = (): CurrentUser | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

let currentUserSnapshot: CurrentUser | null = readStoredUser();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  // Keep other browser tabs in sync too
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) {
      currentUserSnapshot = readStoredUser();
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

const getSnapshot = () => currentUserSnapshot;

/** Persist the signed-in user and update every component using useAuth. */
export const setCurrentUser = (user: CurrentUser | Record<string, any> | null) => {
  if (user) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  currentUserSnapshot = user as CurrentUser | null;
  notify();
};

export const useAuth = () => {
  const currentUser = useSyncExternalStore(subscribe, getSnapshot);
  const navigate = useNavigate();

  // State is read synchronously from localStorage, so it is never "loading".
  // Kept in the return value for existing callers.
  const loading = false;

  const signOut = () => {
    setCurrentUser(null);
    navigate('/signin', { replace: true });
  };

  const isAuthenticated = () => {
    return currentUser !== null && (currentUser?.isAuthenticated === true || !!currentUser?.id);
  };

  const isAdmin = () => {
    return currentUser?.role === 'admin';
  };

  const isModerator = () => {
    return currentUser?.role === 'moderator' || isAdmin();
  };

  return {
    currentUser,
    loading,
    signOut,
    isAuthenticated,
    isAdmin,
    isModerator
  };
};
